import { FeatureCollection, Point } from 'geojson'
import RobotOnMapGs40 from 'images/for_map.robot_gs_40.png'
import RobotOnMapGs75 from 'images/for_map.robot_gs_75.png'
import RobotOnMapGsOmnie from 'images/for_map.robot_gs_omnie.png'
import RobotOnMapGsPhantas from 'images/for_map.robot_gs_phantas.png'
import RobotOnMapBlueSw from 'images/robot_on_map_blue_sw.png'
import RobotOnMapBlueSwsc from 'images/robot_on_map_blue_swsc.png'
import RobotOnMapGreenSw from 'images/robot_on_map_green_sw.png'
import RobotOnMapGreenSwsc from 'images/robot_on_map_green_swsc.png'
import RobotOnMapOrangeSw from 'images/robot_on_map_orange_sw.png'
import RobotOnMapOrangeSwsc from 'images/robot_on_map_orange_swsc.png'
import RobotOnMapPinkSw from 'images/robot_on_map_pink_sw.png'
import RobotOnMapPinkSwsc from 'images/robot_on_map_pink_swsc.png'
import RobotOnMapWhite from 'images/robot_on_map_white.png'
import RobotOnMapYellowSw from 'images/robot_on_map_yellow_sw.png'
import RobotOnMapYellowSwsc from 'images/robot_on_map_yellow_swsc.png'
import { VENDOR, VendorCode } from 'utils/vendorDisplayName'

export const loadRobotImage = (map: mapboxgl.Map | undefined, imageName: string = 'robot') => {
  if (map?.hasImage(imageName)) {
    return
  }
  map?.loadImage(RobotOnMapPinkSwsc, (error, image) => {
    if (error || !image) {
      return
    }
    if (!map?.hasImage(imageName)) {
      map?.addImage(imageName, image)
    }
  })
}

export const loadAllRobotImages = (map: mapboxgl.Map | undefined) => {
  if (!map) return

  const imagesToLoad = [
    { name: 'robot-blue-sw', image: RobotOnMapBlueSw },
    { name: 'robot-blue-swsc', image: RobotOnMapBlueSwsc },
    { name: 'robot-green-sw', image: RobotOnMapGreenSw },
    { name: 'robot-green-swsc', image: RobotOnMapGreenSwsc },
    { name: 'robot-pink-sw', image: RobotOnMapPinkSw },
    { name: 'robot-pink-swsc', image: RobotOnMapPinkSwsc },
    { name: 'robot-yellow-sw', image: RobotOnMapYellowSw },
    { name: 'robot-yellow-swsc', image: RobotOnMapYellowSwsc },
    { name: 'robot-orange-sw', image: RobotOnMapOrangeSw },
    { name: 'robot-orange-swsc', image: RobotOnMapOrangeSwsc },
    { name: 'robot-white-sw', image: RobotOnMapWhite },
    { name: 'robot-white-swsc', image: RobotOnMapWhite },
    // Gausium / GS robots
    { name: 'robot-gs-40', image: RobotOnMapGs40 },
    { name: 'robot-gs-omnie', image: RobotOnMapGsOmnie },
    { name: 'robot-gs-75', image: RobotOnMapGs75 },
    { name: 'robot-gs-phantas', image: RobotOnMapGsPhantas },
    // Backwards compatibility or generic
    { name: 'robot-white', image: RobotOnMapWhite },
    { name: 'robot-blue', image: RobotOnMapBlueSwsc },
    { name: 'robot-green', image: RobotOnMapGreenSwsc },
    { name: 'robot-pink', image: RobotOnMapPinkSwsc },
    { name: 'robot-yellow', image: RobotOnMapYellowSwsc },
    { name: 'robot-orange', image: RobotOnMapOrangeSwsc },
  ]

  imagesToLoad.forEach(({ name, image }) => {
    if (map.hasImage(name)) return

    map.loadImage(image, (error, img) => {
      if (error || !img) return
      if (map.hasImage(name)) return
      map.addImage(name, img)
    })
  })
}

/**
 * Returns the icon name for a robot based on its color and type
 */
export const getRobotIconName = (vendor?: VendorCode | string, color?: string, type?: string): string => {
  if (vendor === VENDOR.GAUSIUM) {
    const gsType = (type || '').toString().trim()
    const gsTypeUpper = gsType.toUpperCase()

    // Canonical GS codes from backend mapping:
    // 75=Scrubber 75, 40=Vacuum 40, 50=Omnie, S=Phantas
    if (gsType === '75') return 'robot-gs-75'
    if (gsType === '40') return 'robot-gs-40'
    if (gsType === '50') return 'robot-gs-omnie'
    if (gsTypeUpper === 'S') return 'robot-gs-phantas'

    return 'robot-gs-75'
  }

  const robotType = type?.toLowerCase() || ''
  const isSwsc = robotType === 'combo' || robotType.includes('scrub')

  // White robots only have one variant for now, based on images list
  if (color === 'White') {
    return 'robot-white'
  }

  const typeSuffix = isSwsc ? '-swsc' : '-sw'
  let iconColor = 'pink'

  switch (color) {
    case 'Blue':
      iconColor = 'blue'
      break
    case 'Green':
      iconColor = 'green'
      break
    case 'Yellow':
      iconColor = 'yellow'
      break
    case 'Orange':
      iconColor = 'orange'
      break
    case 'Pink':
    default:
      iconColor = 'pink'
  }

  return `robot-${iconColor}${typeSuffix}`
}

export const setMapCursor = (map: mapboxgl.Map | undefined, cursor: string) => {
  if (map) {
    map.getCanvas().style.cursor = cursor
  }
}

// Create arrow icon for rotation handle marker
export const createRotationArrowImage = (): HTMLImageElement | null => {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 64
    canvas.height = 64
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Draw circle with two arrows indicating rotation direction (clockwise)
    ctx.fillStyle = '#9747FF'
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 2.5

    const centerX = 32
    const centerY = 32
    const radius = 10
    const arrowDistance = 12 // Distance from circle center to arrowhead start
    const arrowHeadSize = 8 // Arrowhead size (comparable to circle radius)
    const arrowWidth = 4 // Arrowhead width

    // Draw circle
    ctx.beginPath()
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()

    // Function to draw only triangular arrowhead (without line)
    const drawArrow = (angle: number) => {
      // Arrowhead tip (farther from center)
      const tipX = centerX + Math.cos(angle) * (arrowDistance + arrowHeadSize)
      const tipY = centerY - Math.sin(angle) * (arrowDistance + arrowHeadSize)

      // Arrow direction vector (from center outward)
      const unitX = Math.cos(angle)
      const unitY = -Math.sin(angle)

      // Perpendicular vector for arrowhead width
      const perpX = -unitY
      const perpY = unitX

      // Triangle base (closer to center)
      const baseX = centerX + Math.cos(angle) * arrowDistance
      const baseY = centerY - Math.sin(angle) * arrowDistance

      // Triangle base points
      const basePoint1X = baseX + perpX * arrowWidth
      const basePoint1Y = baseY + perpY * arrowWidth
      const basePoint2X = baseX - perpX * arrowWidth
      const basePoint2Y = baseY - perpY * arrowWidth

      // Draw only triangular arrowhead with black outline
      ctx.strokeStyle = '#000000'
      ctx.lineWidth = 2
      ctx.fillStyle = '#ffffff'

      ctx.beginPath()
      ctx.moveTo(tipX, tipY) // Tip
      ctx.lineTo(basePoint1X, basePoint1Y) // Left base point
      ctx.lineTo(basePoint2X, basePoint2Y) // Right base point
      ctx.closePath()
      ctx.fill() // White fill
      ctx.stroke() // Black outline
    }

    // Draw first arrow (up, 0° or north)
    const angle1 = 0
    drawArrow(angle1)

    // Draw second arrow (down, 180° or south) - 180 degrees difference
    const angle2 = Math.PI
    drawArrow(angle2)

    // Convert canvas to image
    const img = new Image()
    img.src = canvas.toDataURL()
    return img
  } catch (error) {
    console.error('Error creating rotation arrow image', error)
    return null
  }
}

export const loadRotationHandleImage = (map: mapboxgl.Map | undefined) => {
  if (map?.hasImage('rotation-handle')) {
    return
  }
  const arrowImage = createRotationArrowImage()
  if (arrowImage) {
    arrowImage.onload = () => {
      if (!map?.hasImage('rotation-handle')) {
        map?.addImage('rotation-handle', arrowImage)
      }
    }
  }
}

export const createRobotPointData = (
  position: Point | undefined,
  yaw: number,
  icon?: string
): FeatureCollection<Point> => {
  if (!position) {
    return {
      type: 'FeatureCollection',
      features: [],
    }
  }
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: position.coordinates,
        },
        properties: {
          yaw,
          icon,
        },
      },
    ],
  }
}
